import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import CAProvinces from "../../../containers/AddressContainer/provinces.json";
import USStates from "../../../containers/AddressContainer/states.json";
import {
  AddressFormType,
  AddressType,
  AddressTypeEnum,
  Country,
  Language,
  LANGUAGE,
} from "../../../types";
import { AddressBox } from "../../AddressBox";
import { Icon } from "../../../common/components/Icon";
import { IconButton, SelectChangeEvent, Typography } from "@mui/material";
import {
  formatAddressForTools,
  userAddressFormTypeToUserAddress,
} from "../../../utils/userAddress.utils";
import {
  getEmptyAddress,
  userAddressToUserAddressFormType,
} from "../../../utils/userAddress.utils";
import {
  Box,
  CopyButton,
  FlexColBox,
  Input,
  Select,
} from "../../../common/components";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit_icon.svg";
import { palette } from "../../../styles/theme";

interface Props {
  initialValue: AddressType;
  titleLabel: string;
  onUpdate?: (values: AddressFormType) => Promise<void>;
  addressType: AddressTypeEnum;
  fromAdmin?: boolean;
}

const EditableInputAddressField = ({
  initialValue,
  onUpdate,
  titleLabel,
  addressType,
  fromAdmin,
}: Props) => {
  const { t } = useTranslation();
  const [isEditing, setEditing] = useState(false);
  const [valueState, setValueState] = useState<AddressFormType>({
    organization: "",
    firstName: "",
    lastName: "",
    address: "",
    appartment: "",
    city: "",
    country: "CA",
    state: "",
    zipcode: "",
    phone: "",
    // phoneExt: "",
    email: "",
    ein: "",
    type: addressType,
  });

  //Initial loading of the address
  useEffect(() => {
    //Since the input from the formatted preorder address is different than the input of the API to update address,
    //we need to transform the initialValue to match the API schema
    const initialValueFormattedforAPI =
      userAddressToUserAddressFormType(initialValue);

    setValueState(
      initialValue.id === "default" &&
        addressType === AddressTypeEnum.BILLING &&
        initialValue.address
        ? getEmptyAddress(initialValue.address.country)
        : initialValueFormattedforAPI
    );
    // dont want to trigger this effect on every language change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressType, initialValue, setValueState]);

  const onClickEdit = useCallback(() => {
    setEditing(true);
  }, [setEditing]);

  const onClickSave = useCallback(() => {
    onUpdate?.(valueState).then(() => {
      setEditing(false);
    });
  }, [setEditing, onUpdate, valueState]);

  const onClickCancel = useCallback(() => {
    //Reset to original values
    const initialValueFormattedforAPI =
      userAddressToUserAddressFormType(initialValue);

    setValueState(initialValueFormattedforAPI);

    setEditing(false);
  }, [setValueState, setEditing, initialValue]);

  const onInputTextValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof Omit<
      AddressFormType,
      "country" | "type" | "communicationLanguage"
    >
  ) => {
    setValueState((oldValueState) => {
      const newState = _.cloneDeep(oldValueState);

      const value = e.target.value || "";

      newState[field] = value;
      return newState;
    });
  };

  const onInputCountryValueChange = (e: SelectChangeEvent<Country>) => {
    setValueState((oldValueState) => {
      const newState = _.cloneDeep(oldValueState);

      const newCountry = e.target.value;

      const value = newCountry || "";

      newState["country"] = value as Country;

      // Update the states and select the first one
      const stateList = newCountry === "US" ? USStates : CAProvinces;
      newState["state"] = stateList[0];

      return newState;
    });
  };

  const onInputCommunicationLanguageValueChange = (
    e: SelectChangeEvent<Language>
  ) => {
    setValueState((oldValueState) => {
      const newState = _.cloneDeep(oldValueState);
      newState["communicationLanguage"] = e.target.value as Language;
      return newState;
    });
  };

  //It will work because we replace the valueState object on each address changes
  const isValid = useMemo(() => {
    const schema = yup.object().shape({
      organization: yup.string(),
      firstName: yup.string().required(),
      lastName: yup.string().required(),
      address: yup.string().required(),
      appartment: yup.string(),
      city: yup.string().required(),
      country: yup.string().required(),
      state: yup.string().required(),
      zipcode: yup.string().required(),
      phone: yup.string().required(),
      email: yup.string().required(),
    });
    return schema.isValidSync(valueState);
  }, [valueState]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      onClickSave();
    }
    if (e.key === "Escape") {
      onClickCancel();
    }
  };

  return (
    <FlexColBox gap="8px">
      <Box display="flex" alignItems="center" gap="8px" minHeight="21px">
        <Typography variant="textRegularBold">{titleLabel}</Typography>
        {!isEditing &&
          fromAdmin &&
          addressType === AddressTypeEnum.SHIPPING && (
            <CopyButton
              value={formatAddressForTools(
                userAddressFormTypeToUserAddress(valueState)
              )}
            />
          )}
        {onUpdate && !isEditing && !fromAdmin && (
          <EditIcon onClick={onClickEdit} style={{ cursor: "pointer" }} />
        )}
        {isEditing && (
          <>
            <IconButton onClick={onClickSave} size="small" disabled={!isValid}>
              <Icon icon="check" />
            </IconButton>
            <IconButton onClick={onClickCancel} size="small">
              <Icon icon="cancel" />
            </IconButton>
          </>
        )}
      </Box>
      <Box>
        {isEditing ? (
          <FlexColBox gap="8px">
            <Input
              onChange={(e) => onInputTextValueChange(e, "organization")}
              value={valueState.organization}
              placeholder={t("checkout.organization")}
              autoComplete="organization"
              required
              onKeyDown={onKeyDown}
            />
            <Input
              onChange={(e) => onInputTextValueChange(e, "firstName")}
              value={valueState.firstName}
              placeholder={t("checkout.first_name")}
              autoComplete="given-name"
              required
              onKeyDown={onKeyDown}
            />
            <Input
              onChange={(e) => onInputTextValueChange(e, "lastName")}
              value={valueState.lastName}
              placeholder={t("checkout.last_name")}
              autoComplete="family-name"
              required
              onKeyDown={onKeyDown}
            />
            {addressType === AddressTypeEnum.SHIPPING && (
              <Select
                onChange={(e) =>
                  onInputCommunicationLanguageValueChange(e as any)
                }
                value={valueState.communicationLanguage}
                options={Object.values(LANGUAGE).map((option) => ({
                  label: t(`general.language.${option}`),
                  value: option,
                }))}
              />
            )}
            <Input
              onChange={(e) => onInputTextValueChange(e, "address")}
              value={valueState.address}
              placeholder={t("checkout.addressLine1")}
              autoComplete="street-address"
              required
              onKeyDown={onKeyDown}
            />
            <Input
              onChange={(e) => onInputTextValueChange(e, "appartment")}
              value={valueState.appartment}
              placeholder={t("checkout.addressLine2")}
              onKeyDown={onKeyDown}
            />
            <Input
              onChange={(e) => onInputTextValueChange(e, "city")}
              value={valueState.city}
              placeholder={t("checkout.city")}
              autoComplete="address-level2"
              required
              onKeyDown={onKeyDown}
            />
            <Select
              onChange={(e) => onInputCountryValueChange(e as any)}
              value={valueState.country}
              autoComplete="country"
              options={["CA", "US"].map((option) => ({
                label: t(`general.country.${option}`),
                value: option,
              }))}
            />
            <Input
              onChange={(e) => onInputTextValueChange(e, "ein")}
              value={valueState.ein}
              placeholder={t("checkout.ein")}
              onKeyDown={onKeyDown}
            />
            <Select
              onChange={(e) => onInputTextValueChange(e as any, "state")}
              value={valueState.state}
              autoComplete="address-level1"
              options={(valueState.country === "US"
                ? USStates
                : CAProvinces
              ).map((option) => ({
                label: t(`alphaCodes.${option}`),
                value: option,
              }))}
            />
            <Input
              onChange={(e) => onInputTextValueChange(e, "zipcode")}
              value={valueState.zipcode}
              placeholder={
                valueState.country === "US"
                  ? t("checkout.zip_code")
                  : t("checkout.postal_code")
              }
              autoComplete="postal-code"
              required
              onKeyDown={onKeyDown}
            />
            <Input
              onChange={(e) => onInputTextValueChange(e, "phone")}
              value={valueState.phone}
              placeholder={t("general.phone")}
              autoComplete="phone"
              required
              onKeyDown={onKeyDown}
            />
            <Input
              onChange={(e) => onInputTextValueChange(e, "email")}
              value={valueState.email}
              placeholder={t("checkout.email")}
              autoComplete="email"
              type={"email"}
              required
              onKeyDown={onKeyDown}
            />
          </FlexColBox>
        ) : (
          <Box sx={{ "& *": { color: palette.grey.primary } }}>
            <AddressBox
              address={initialValue}
              addressType={addressType}
              fromAdmin={fromAdmin}
              hideCopyButton={!fromAdmin}
            />
          </Box>
        )}
      </Box>
    </FlexColBox>
  );
};

export default EditableInputAddressField;
